import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import DispatchItemManager from './DispatchItemManager';
import CalendarItemManager from './CalendarItemManager';
import CustomSectionBuilder from './CustomSectionBuilder';
import LinksSectionBuilder from './LinksSectionBuilder';
import GallerySectionBuilder from './GallerySectionBuilder';
import VideoSectionBuilder from './VideoSectionBuilder';
import AttachmentSectionBuilder from './AttachmentSectionBuilder';
import ContactFormBuilder from './ContactFormBuilder';
import EventCalendarSectionBuilder from './EventCalendarSectionBuilder';
import SocialFeedSectionBuilder from './SocialFeedSectionBuilder';

/**
 * SectionEditor - Configure individual portal sections
 * Handles section visibility, title, ordering, and section-specific settings
 */
export default function SectionEditor({
  section,
  portalData,
  teamId,
  allSections = [],
  onUpdate,
  onDelete,
  onSettingsUpdate,
  onCollectionUpdate,
  isActive,
  onActivate
}) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(section.title);
  const [titleError, setTitleError] = useState('');
  const [linkAddTrigger, setLinkAddTrigger] = useState(0);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const sortableStyle = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
      cursor: isDragging ? 'grabbing' : 'default'
    }),
    [transform, transition, isDragging]
  );

  const getSectionIcon = (type) => {
    const icons = {
      dispatches: '📨',
      wiki: '📝',
      calendar: '📅',
      roster: '👥',
      files: '📁',
      snacks: '🍪',
      custom: '✨',
      links: '🔗',
      gallery: '🖼️',
      videos: '🎬',
      attachments: '📎',
      contact_form: '✉️',
      event_calendar: '🗓️',
      social_feed: '📣'
    };
    return icons[type] || '📄';
  };

  const handleTitleSave = () => {
    const trimmed = titleValue.trim();

    if (!trimmed) {
      setTitleValue(section.title);
      setEditingTitle(false);
      setTitleError('');
      return;
    }

    const normalized = trimmed.toLowerCase();
    const hasDuplicate = allSections.some((s) => {
      if (s.id === section.id) return false;
      const comparison = (s.title || '').trim().toLowerCase();
      return comparison === normalized;
    });

    if (hasDuplicate) {
      setTitleError('Another section already uses this title.');
      return;
    }

    setTitleError('');
    onUpdate({ title: trimmed });
    setEditingTitle(false);
  };

  const handleVisibilityToggle = async () => {
    const newVisibility = !section.visible;
    
    // For roster, files, and snacks - update the actual team settings
    if (['roster', 'files', 'snacks'].includes(section.section_type) && teamId) {
      try {
        const settingField = section.section_type === 'roster' ? 'portal_roster_public' 
          : section.section_type === 'files' ? 'portal_files_public'
          : 'portal_snacks_public';
        
        const { error } = await supabase
          .from('teams')
          .update({ [settingField]: newVisibility })
          .eq('id', teamId);
        
        if (error) throw error;
        
        toast.success(`${section.title} ${newVisibility ? 'enabled' : 'disabled'} on portal`);
        
        // Refresh portal data to reflect the settings change
        if (onSettingsUpdate) {
          await onSettingsUpdate();
        }
      } catch (error) {
        console.error('Error updating portal settings:', error);
        toast.error('Failed to update portal settings');
        return; // Don't update section visibility if settings update failed
      }
    }
    
    // Update section visibility
    onUpdate({ visible: newVisibility });
  };

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className={`border rounded-lg ${
        isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
      } transition-colors ${isDragging ? 'shadow-xl z-10' : ''}`}
    >
      {/* Section Header */}
      <div className="p-3 sm:p-4">
        {/* Mobile: Title on first line, controls on second line */}
        <div className="sm:hidden">
          <div className="flex items-center gap-2 mb-2">
            {/* Icon */}
            <span className="text-xl shrink-0">{getSectionIcon(section.section_type)}</span>

            {/* Title */}
            {editingTitle ? (
              <input
                type="text"
                value={titleValue}
                onChange={(e) => {
                  setTitleValue(e.target.value);
                  if (titleError) {
                    setTitleError('');
                  }
                }}
                onBlur={handleTitleSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') {
                    setTitleValue(section.title);
                    setEditingTitle(false);
                    setTitleError('');
                  }
                }}
                autoFocus
                className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <h3
                onClick={() => {
                  setEditingTitle(true);
                  setTitleValue(section.title);
                  setTitleError('');
                }}
                className="text-sm font-semibold text-gray-900 cursor-pointer hover:text-blue-600 flex-1"
                title="Click to edit title"
              >
                {section.title}
              </h3>
            )}
          {titleError && (
            <p className="text-xs text-red-600 mb-2">{titleError}</p>
          )}
          </div>

          {/* Controls on second line */}
          <div className="flex items-center gap-1 flex-wrap">
            {/* Drag Handle */}
            <button
              {...listeners}
              {...attributes}
              className="px-2.5 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors touch-manipulation"
              title="Drag to reorder"
              aria-label="Drag section to reorder"
              type="button"
            >
              ☰
            </button>

            {/* Visibility Toggle */}
            <button
              onClick={handleVisibilityToggle}
              className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors touch-manipulation ${
                section.visible 
                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              title={section.visible ? 'Click to hide from portal' : 'Click to show on portal'}
            >
              {section.visible ? '✓ Visible' : '✕ Hidden'}
            </button>

            {/* Expand/Collapse */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="px-2.5 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors touch-manipulation"
              title={isExpanded ? 'Collapse settings' : 'Expand settings'}
            >
              {isExpanded ? '▲ Collapse' : '▼ Expand'}
            </button>

            {/* Delete */}
            {['custom', 'links', 'gallery', 'videos', 'attachments', 'contact_form', 'event_calendar', 'social_feed'].includes(section.section_type) && (
              <button
                onClick={onDelete}
                className="px-2.5 py-1.5 text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 rounded transition-colors touch-manipulation"
                title={`Delete this ${section.section_type} section`}
              >
                🗑️
              </button>
            )}

            {section.section_type === 'links' && (
              <button
                onClick={() => {
                  if (!isExpanded) {
                    setIsExpanded(true);
                  }
                  setLinkAddTrigger((prev) => prev + 1);
                  onActivate?.();
                }}
                className="px-2.5 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors touch-manipulation"
              >
                + Add Link
              </button>
            )}
          </div>
        </div>

        {/* Desktop/Tablet: Original layout with title and controls on same line */}
        <div className="hidden sm:flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Icon */}
            <span className="text-2xl shrink-0">{getSectionIcon(section.section_type)}</span>

            {/* Title */}
            {editingTitle ? (
              <input
                type="text"
                value={titleValue}
                onChange={(e) => {
                  setTitleValue(e.target.value);
                  if (titleError) {
                    setTitleError('');
                  }
                }}
                onBlur={handleTitleSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') {
                    setTitleValue(section.title);
                    setEditingTitle(false);
                    setTitleError('');
                  }
                }}
                autoFocus
                className="flex-1 px-2 py-1 text-base border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <h3
                onClick={() => {
                  setEditingTitle(true);
                  setTitleValue(section.title);
                  setTitleError('');
                }}
                className="text-base font-semibold text-gray-900 cursor-pointer hover:text-blue-600 truncate"
                title="Click to edit title"
              >
                {section.title}
              </h3>
            )}

          </div>

          {titleError && (
            <p className="text-xs text-red-600 mt-1 whitespace-nowrap">{titleError}</p>
          )}

          {/* Controls */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Drag Handle */}
            <button
              {...listeners}
              {...attributes}
              className="px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors touch-manipulation"
              title="Drag to reorder"
              aria-label="Drag section to reorder"
              type="button"
            >
              ☰
            </button>

            {/* Visibility Toggle */}
            <button
              onClick={handleVisibilityToggle}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors touch-manipulation ${
                section.visible 
                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              title={section.visible ? 'Click to hide from portal' : 'Click to show on portal'}
            >
              {section.visible ? '✓ Visible' : '✕ Hidden'}
            </button>

            {/* Expand/Collapse */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors touch-manipulation"
              title={isExpanded ? 'Collapse settings' : 'Expand settings'}
            >
              {isExpanded ? '▲ Collapse' : '▼ Expand'}
            </button>

            {/* Delete */}
            {['custom', 'links', 'gallery', 'videos', 'attachments', 'contact_form', 'event_calendar', 'social_feed'].includes(section.section_type) && (
              <button
                onClick={onDelete}
                className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 rounded transition-colors touch-manipulation"
                title={`Delete this ${section.section_type} section`}
              >
                🗑️
              </button>
            )}

            {section.section_type === 'links' && (
              <button
                onClick={() => {
                  if (!isExpanded) {
                    setIsExpanded(true);
                  }
                  setLinkAddTrigger((prev) => prev + 1);
                  onActivate?.();
                }}
                className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors touch-manipulation"
              >
                + Add Link
              </button>
            )}
          </div>
        </div>

        {/* Section Type Label */}
        <div className="mt-2 text-xs text-gray-500">
          {section.section_type === 'dispatches' && 'Email dispatches sent to parents'}
          {section.section_type === 'wiki' && 'Team reference information'}
          {section.section_type === 'calendar' && 'Upcoming events from calendar'}
          {section.section_type === 'roster' && 'Team roster'}
          {section.section_type === 'files' && 'Shared files and documents'}
          {section.section_type === 'snacks' && 'Snack schedule'}
          {section.section_type === 'custom' && 'Custom HTML content'}
          {section.section_type === 'links' && 'Important links and resources'}
          {section.section_type === 'gallery' && 'Curated image gallery'}
          {section.section_type === 'videos' && 'Embedded highlight videos'}
          {section.section_type === 'attachments' && 'Downloadable files and PDFs'}
          {section.section_type === 'contact_form' && 'Collect inquiries from families'}
          {section.section_type === 'event_calendar' && 'Advanced calendar view with custom grouping'}
          {section.section_type === 'social_feed' && 'Social media embeds and profile links'}
          <span className="inline-flex items-center gap-1 ml-2 text-[11px] text-blue-600">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
            Layout: {section.section_config?.layout === 'sidebar' ? 'Sidebar column' : 'Full width'}
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50">
          {/* Layout Toggle */}
          <div className="mb-4 pb-4 border-b border-gray-200">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              Section Layout
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => onUpdate({ 
                  section_config: { ...section.section_config, layout: 'main' } 
                })}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm rounded border touch-manipulation ${
                  section.section_config?.layout !== 'sidebar'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                📄 Full Width
              </button>
              <button
                onClick={() => onUpdate({ 
                  section_config: { ...section.section_config, layout: 'sidebar' } 
                })}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm rounded border touch-manipulation ${
                  section.section_config?.layout === 'sidebar'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                📊 Sidebar
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {section.section_config?.layout === 'sidebar' 
                ? 'This section will appear in a narrow sidebar column'
                : 'This section will span the full width of the page'}
            </p>
          </div>

          {section.section_type === 'dispatches' && (
            <DispatchItemManager
              dispatches={portalData?.dispatches || []}
              config={section.section_config || {}}
              onConfigUpdate={(config) => onUpdate({ section_config: config })}
            />
          )}

          {section.section_type === 'calendar' && (
            <CalendarItemManager
              events={portalData?.calendar_events || []}
              config={section.section_config || {}}
              onConfigUpdate={(config) => onUpdate({ section_config: config })}
            />
          )}

          {section.section_type === 'custom' && (
            <CustomSectionBuilder
              content={section.section_config?.custom_html || ''}
              onContentChange={(html) =>
                onUpdate({ section_config: { ...section.section_config, custom_html: html } })
              }
              teamId={teamId}
            />
          )}

          {section.section_type === 'links' && (
            <LinksSectionBuilder
              teamId={teamId}
              sectionId={section.id}
              links={portalData?.portal_links || []}
              onLinksUpdate={(updatedLinks) => onCollectionUpdate?.('portal_links', section.id, updatedLinks)}
              addTrigger={linkAddTrigger}
            />
          )}

          {section.section_type === 'gallery' && (
            <GallerySectionBuilder
              teamId={teamId}
              sectionId={section.id}
              items={portalData?.portal_gallery_items || []}
              onItemsUpdate={(_, updated) => onCollectionUpdate?.('portal_gallery_items', section.id, updated)}
            />
          )}

          {section.section_type === 'videos' && (
            <VideoSectionBuilder
              teamId={teamId}
              sectionId={section.id}
              items={portalData?.portal_video_items || []}
              onItemsUpdate={(_, updated) => onCollectionUpdate?.('portal_video_items', section.id, updated)}
            />
          )}

          {section.section_type === 'attachments' && (
            <AttachmentSectionBuilder
              teamId={teamId}
              sectionId={section.id}
              items={portalData?.portal_attachment_items || []}
              onItemsUpdate={(_, updated) => onCollectionUpdate?.('portal_attachment_items', section.id, updated)}
            />
          )}

          {section.section_type === 'contact_form' && (
            <ContactFormBuilder
              teamId={teamId}
              sectionId={section.id}
              config={section.section_config?.contactForm}
              onConfigChange={(contactForm) =>
                onUpdate({
                  section_config: {
                    ...section.section_config,
                    contactForm
                  }
                })
              }
            />
          )}

          {section.section_type === 'event_calendar' && (
            <EventCalendarSectionBuilder
              config={section.section_config?.eventCalendar}
              onConfigChange={(eventCalendar) =>
                onUpdate({
                  section_config: {
                    ...section.section_config,
                    eventCalendar
                  }
                })
              }
            />
          )}

          {section.section_type === 'social_feed' && (
            <SocialFeedSectionBuilder
              teamId={teamId}
              sectionId={section.id}
              items={portalData?.portal_social_links || []}
              onItemsUpdate={(_, updated) => onCollectionUpdate?.('portal_social_links', section.id, updated)}
            />
          )}

          {section.section_type === 'wiki' && (
            <div className="text-sm text-gray-600">
              <p className="mb-2">Wiki items are managed in the Wiki Editor.</p>
              <p>You can control visibility and pinning from there.</p>
            </div>
          )}

          {['roster', 'files', 'snacks'].includes(section.section_type) && (
            <div className="text-sm">
              <p className="text-gray-700 mb-3">
                This section displays {section.section_type} from your team.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-800 text-xs font-medium mb-1">✨ Auto-Updated</p>
                <p className="text-blue-700 text-xs">
                  Toggling visibility for this section automatically updates your team's Portal Settings. 
                  When visible, {section.section_type === 'roster' ? 'player names and positions' 
                    : section.section_type === 'files' ? 'uploaded files' 
                    : 'the snack schedule'} will be publicly accessible on your portal.
                </p>
              </div>
              {section.section_type === 'files' && (
                <div className="mt-3">
                  <button
                    onClick={() => navigate('/files')}
                    className="text-xs px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    📁 Manage Files →
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Upload files to share with parents on the portal
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Display Count Slider (for auto-generated sections) */}
          {['dispatches', 'calendar'].includes(section.section_type) && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Count: {section.section_config?.limit || 10}
              </label>
              <input
                type="range"
                min="3"
                max="20"
                value={section.section_config?.limit || 10}
                onChange={(e) => onUpdate({
                  section_config: { ...section.section_config, limit: parseInt(e.target.value) }
                })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>3</span>
                <span>20</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

